import axios from "axios";

// Define constants
const weeklyMap = {
    "sun": "อาทิตย์",
    "mon": "จันทร์",
    "tue": "อังคาร",
    "wed": "พุธ",
    "thu": "พฤหัสบดี",
    "fri": "ศุกร์",
    "sat": "เสาร์"
}
const timeMap = {
    "EarlyMorning": "ช่วงเวลา 08:00 - 12:00 น.",
    "MidDay": "ช่วงเวลา 13:00 - 17:00 น.",
    "LateEvening": "ช่วงเวลา 18:00 - 22:00 น."
}
const serverUrl = process.env.LIFF_OPTIMUS_SERVER_URL || '';

const daysOfWeek = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

type WeeklyMask = {
    sun: boolean, mon: boolean, tue: boolean,
    wed: boolean, thu: boolean, fri: boolean,
    sat: boolean
}

type ContractInfo = {
    contract_id: number,
    account_type?: string,
    loan_id?: string,
    product_code?: string,
    loan_status?: string,
    last_info_updated_date?: string,  // ISO Format
    due_date_dom?: number
}

type ReminderItemData = {
    reminder_id?: string,
    repetition_type: "Monthly" | "Weekly",
    repetition_slot: "EarlyMorning" | "MidDay" | "LateEvening",
    repetition_limit: "NoRepetition" | "Months",
    reference_datetime: string,  // ISO Format
    repetition_limit_datetime: string,  // ISO Format
    repetition_limit_offset?: number,
    message: string,
    specific_time_day_of_month?: Array<number>,
    specific_time_day_of_week?: WeeklyMask
};
const datePattern = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/;

// Utility Functions
export const Utils = {
    isISO: (value: any): boolean => {
        return (typeof value === 'string' && datePattern.exec(value)) ? true : false;
    },
    getTodayEndMonth: (monthOffset: number): Date => {
        let endMonth = (new Date());
        endMonth.setMonth(endMonth.getMonth() + monthOffset);
        return new Date(endMonth.getFullYear(), endMonth.getMonth() + 1, 0);
    },
    toActiveDayOfWeekMask: (dow: string): WeeklyMask => {
        return ({
            "sun": dow == daysOfWeek[0],
            "mon": dow == daysOfWeek[1],
            "tue": dow == daysOfWeek[2],
            "wed": dow == daysOfWeek[3],
            "thu": dow == daysOfWeek[4],
            "fri": dow == daysOfWeek[5],
            "sat": dow == daysOfWeek[6]
        })
    }
}

// Define Service / Logic
export const OptimusReminderService = {
    fetchLoanInfo: async () => {
        const res = await axios.post(`${serverUrl}/v1/information/loan-info`, {
            "line_token": `${window.sessionStorage.getItem('accessToken')}`,
        }, {
            timeout: 30_000,
            headers: {
                Authorization: `Bearer ${window.sessionStorage.getItem('accessToken')}`,
            }
        });
        return res.data["contracts"] satisfies Array<ContractInfo>
    },

    fetchReminderList: async () => {
        const res = await axios.post(`${serverUrl}/v1/reminder/get-self-reminders`, {
            "line_token": `${window.sessionStorage.getItem('accessToken')}`,
        }, {
            timeout: 30_000,
            headers: {
                Authorization: `Bearer ${window.sessionStorage.getItem('accessToken')}`,
            }
        });
        return res.data satisfies Array<ReminderItemData>;
    },

    createReminderItem: async (data: ReminderItemData) => {
        if(
            !Utils.isISO(data["reference_datetime"]) ||
            !Utils.isISO(data["repetition_limit_datetime"])
        ) {
            // Type guard
            return null;  // Invalid data
        }
        data["line_token"] = `${window.sessionStorage.getItem('accessToken')}`
        return await axios.post(`${serverUrl}/v1/reminder/write-self-reminder`, data, {
            timeout: 30_000,
            headers: {
                Authorization: `Bearer ${window.sessionStorage.getItem('accessToken')}`,
            }
        })
    },

    deleteReminderItem: async (reminder_id: string | null) => {
        if(reminder_id === null) return;

        const data = {
            line_token: `${window.sessionStorage.getItem('accessToken')}`,
            config_ids: [reminder_id]
        }
        return await axios.post(`${serverUrl}/v1/reminder/delete-self-reminder-by-ids`, data, {
            headers: {
                Authorization: `Bearer ${window.sessionStorage.getItem('accessToken')}`,
            }
        })
    }
}

export const OptimusReminderMapper = {
    toUIReminderListItem: (item) => {
        const endDate = new Date(item["repetition_limit_datetime"]);
        const endMonthString = endDate.toLocaleString("th-TH", { month: "short", year: '2-digit' });
        const repetitionList = (item["repetition_type"] == "Weekly") ? (
            Array.from(
                Object.keys(item["specific_time_day_of_week"])
            ).filter(
                (key) => item["specific_time_day_of_week"][key]
            ).map((key) => weeklyMap[key])
        ) : item["specific_time_day_of_month"];
        
        return ({
            repetition: (
                (item["repetition_type"] == "Monthly") 
                ? (`เตือนทุกวันที่ ${repetitionList.join(", ")}`) 
                : `เตือนทุกวัน ${repetitionList.join(", ")}`
            ),
            time: timeMap[item["repetition_slot"]],
            until: (
                (item["repetition_limit_offset"] == 0) 
                ? "เตือนแค่ครั้งเดียว" 
                : `เตือนไปจนถึง ${endMonthString}`
            ),
            textMsg: item["message"],
            reminderID: item["reminder_id"]
        })
    },
    prepareCreateReminderItemData: ({
        monthlyRadio,
        timeSlotEarly, timeSlotMidday,
        onceRepRadio, weeklyRadio,
        date, activeWeekDay,
        monthlyRepRadio, monthOffset,
        reminderID
    }): ReminderItemData => {
        const endMonth = Utils.getTodayEndMonth(monthOffset);
        const data = {
            repetition_type: monthlyRadio ? "Monthly" : "Weekly",
            repetition_slot: timeSlotEarly ? "EarlyMorning" : (timeSlotMidday ? "MidDay" : "LateEvening"),
            repetition_limit: onceRepRadio ? "NoRepetition" : "Months",
            reference_datetime: (new Date()).toISOString(),
            repetition_limit_datetime: endMonth.toISOString(),
            message: "ตรวจสอบยอดได้แล้ววันนี้ ตามการตั้งเตือนของคุณ",
        } satisfies ReminderItemData;
        if(monthlyRadio && !weeklyRadio) {
            data["specific_time_day_of_month"] = [date]
        } else if(weeklyRadio && !monthlyRadio) {
            data["specific_time_day_of_week"] = Utils.toActiveDayOfWeekMask(activeWeekDay);
        }
        if(monthlyRepRadio) {
            data["repetition_limit_offset"] = monthOffset
        }
        if(reminderID !== null && reminderID === reminderID) {
            data["reminder_id"] = reminderID;
        }
        return data satisfies ReminderItemData;
    }
}

export type {
    ReminderItemData, ContractInfo
}

export { daysOfWeek };
